<template>
<div v-if="isLoaded" data-component="FilterPricing.vue" class="a-loaded">
	<PageHeader v-if="!isNewCategory" :Back="true" :BackOnDesktop="true" :EmitInstead="true" :Title="'Cena'" @back-click="backClick"/>

	<!-- Kontent sekcji z cenami -->
	<div class="p-5">
		<div class="font-extrabold pb-3">
			Jak wyceniasz swoją usługę:
			<div class="text-lg" v-html="`${pricingInfo.categoryLabel}?`"/>
		</div>
		<div>
			To tylko <span class="font-extrabold">szacunkowe kwoty</span>. Pokażemy je Twoim Klientom po złożeniu zapytania.
		</div>
		<div>
			<div class="font-extrabold py-5" v-html="pricingInfo.helperLabel"/>
			<div class="flex items-center mt">
				<div class="mr-2" v-html="'od'"/>
				<input v-model="pricingInfo.from" type="text" inputmode="numeric" pattern="[0-9]*" placeholder="0" class="text-center basis-16 px-0" maxlength="6" v-on="inputHandlers">
				<div class="mx-2" v-html="'do'"/>
				<input v-model="pricingInfo.to" type="text" inputmode="numeric" pattern="[0-9]*" placeholder="0" class="text-center basis-16 px-0" maxlength="6" v-on="inputHandlers">
				<div class="ml-2" v-html="pricingInfo.unitLabel"/>
			</div>
			<div class="form-input-validation | text-danger text-xs mt-2" v-html="validationMessage"/>
		</div>
	</div>

	<!-- Dolny pasek z przyciskami -->
	<div class="lg:hidden h-24"/>
	<div class="lg:sticky shadow-default z-2 lg:bg-white/50 lg:shadow-white fixed inset-x-0 bottom-0 flex justify-end p-3 bg-white lg:w-max lg:ml-auto">
		<template v-if="isNewCategory">
			<button
				class="btn btn-white lg:w-auto w-1/2 | px-0 lg:px-3 font-normal"
				:disabled="isSaveButtonDisabled"
				@click="onSkipButton"
				v-html="'Przypomnij później'"
			/>
			<div class="mx-1 lg:mx-auto"/>
			<button
				class="btn btn-primary lg:w-auto w-1/2"
				:disabled="isSaveButtonDisabled"
				@click="savePrice(true)"
				v-html="'Dalej'"
			/>
		</template>
		<button
			v-else
			class="btn btn-primary lg:w-auto w-full"
			:disabled="isSaveButtonDisabled"
			@click="$emit('save-button-clicked', { source: 'price' })"
			v-html="isFilterDirty ? 'Zapisz' : 'Gotowe'"
		/>
	</div>
</div>
</template>

<script lang="ts">
import { mapState, mapWritableState } from 'pinia';
import { useServicesStore } from '../../../stores/services';
import { Services } from '../../../types/interfaces/services/Services';
import serverFetch from '../../../globals/ServerFetch';

/**
 * Informacje o cenie usługi
 */
interface FilterPricingData {
	/** Nazwa kategorii, np. "budowa domów" */
	categoryLabel:string,
	/** Tekst pomocniczy, np. "Określ cenę bez kosztu materiału" */
	helperLabel:string,
	/** Tekst pojawiający się za polami do wpisania informacji */
	unitLabel:string,
	/** Wartość "od". Jeżeli wynosi 0, traktujemy ją jako nie podaną. */
	from:number|null,
	/** Wartość "do". Jeżeli wynosi 0, traktujemy ją jako nie podaną. */
	to:number|null,
	/** Identyfikator konkretnej jednostki wyceny na kategorii */
	idCategoryPricingData:number,
	/** Identyfikator wyceny użytkownika */
	id:number,
	/** Identyfikator użytkownika */
	idCustomer:number,
	/** Identyfikator kategorii */
	idCategory:number,
}

export default {
	name: 'FilterPricing',
	props: {
		/**
		 * Główny obiekt usług
		 */
		servicesObject: {
			type: Object as () => Services,
			required: true,
		},

		/**
		 * Początek ścieżki do API.
		 */
		apiNamespace: {
			type: String,
			required: true,
		},

		/**
		 * Identyfikator kategorii dla której ma być edytowana cena.
		 */
		categoryId: {
			type: Number,
			required: true,
		},
	},
	emits: ['filter-change', 'save-button-clicked', 'send-analytics-event'],
	data() {
		return {

			/**
			 * Czy istnieją jakieś błędy walidacji?
			 */
			validationMessage: '',

			/**
			 * Czy załadowano informacje o cenach?
			 */
			isLoaded: false,

			/**
			 * Pobrane z serwera informacje o cenach
			 */
			pricingInfo: {} as FilterPricingData,

			/**
			 * Obsługa zdarzeń pól z wpisaniem ceny
			 */
			inputHandlers: {
				blur: this.onBlur,
				input: this.onInput,
				change: this.onChange,
			},
		};
	},
	computed: {
		...mapState(useServicesStore, [
			'customerId',
		]),
		...mapWritableState(useServicesStore, [
			'currentPage',
			'isNewCategory',
			'pricingCategoryId',
			'isFilterDirty',
		]),

		/**
		 * Czy przycisk do zapisu jest aktywny? Nie powinien być w przypadku,
		 * kiedy jest wyświetlony błąd walidacji
		 */
		isSaveButtonDisabled() {
			return !!this.validationMessage;
		},
	},
	mounted() {
		this.onLoad(this.categoryId);
	},
	methods: {
		async onLoad(categoryId:number) {
			const rsvp = await serverFetch(
				`/${this.apiNamespace}/get-category-pricing.ofer`,
				{
					'customerCategoryPricing.idCategory': categoryId,
					'customerCategoryPricing.idCustomer': this.customerId,
				},
				'POST',
			) as {
				status:string,
				result:FilterPricingData,
			};

			// Na razie wyłączamy funkcjonalność.
			if (true || rsvp.status !== 'OK' || !rsvp.result) {
				if (this.isNewCategory) {
					this.$emit('save-button-clicked', { source: 'no-price-for-this-category' });
				} else {
					this.currentPage = '';
					this.pricingCategoryId = 0;
					this.$router.back();
				}
				return;
			}

			const { result } = rsvp;

			if (Number(result.from) < 1) {
				result.from = null;
			}

			if (Number(result.to) < 1) {
				result.to = null;
			}

			this.pricingInfo = result;
			this.isLoaded = true;
		},

		onChange() {
			if (this.isFilterDirty || !this.isNewCategory) { return; }
			this.$emit('filter-change');
			this.isFilterDirty = true;
		},

		/**
		 * Wyświetla komunikat walidacyjny w momencie, kiedy cena "do" jest niższa od "od",
		 */
		onBlur() {
			const { from, to } = this.pricingInfo;
			this.validationMessage = '';
			if (to && to > 0 && Number(to) < Number(from)) {
				this.validationMessage = 'Cena końcowa nie może być niższa od początkowej';
			}
			if ((from && from < 1) || (to && to < 1) || (from && from > 1000000) || (to && to > 1000000)) {
				this.validationMessage = 'Wpisz liczbę od 1 do 999 999';
			}

			const isFromNotInt = from && String(Number.parseInt(String(from), 10)) !== String(from);
			const isToNotInt = to && String(Number.parseInt(String(to), 10)) !== String(to);
			if (isFromNotInt || isToNotInt) {
				this.validationMessage = 'Wpisz liczbę całkowitą';
			}
		},
		/**
		 * Usuwa nienumeryczne znaki podczas wpisywania wartości
		 * @param e Obiekt zdarzenia
		 */
		onInput(e:InputEvent) {
			this.validationMessage = '';
			const el = (e.target as HTMLInputElement);
			const val = Number.parseInt(
				el.value.replace(/[^0-9.]/g, '').replace(/(\..*?)\..*/g, '$1').replace(/^0[^.]/, '0'),
				10,
			);
			el.value = String(val || '');
		},

		/**
		 * Pomija ekran o cenie podczas dodawania nowej kategorii
		 */
		onSkipButton() {
			this.pricingInfo.from = 0;
			this.pricingInfo.to = 0;
			this.$emit('save-button-clicked', { source: 'price' });
			this.$emit('send-analytics-event', 'Pricing_Skip', 'pricing_skip');
		},

		/**
		 * Zapisuje dane o cenie
		 * @param withEvent Czy po udanym zapisie wysłać event `save-button-clicked`?
		 */
		async savePrice(withEvent = false) {
			const params = Object.keys(this.pricingInfo).reduce((a, c) => (a[`customerCategoryPricing.${c}`] = this.pricingInfo[c], a), {});
			params['customerCategoryPricing.from'] ||= 0;
			params['customerCategoryPricing.to'] ||= 0;

			const rsvp = await serverFetch(
				`/${this.apiNamespace}/set-category-pricing.ofer`,
				params,
				'POST',
			) as {
				status:string,
				validation:string,
			};

			if (rsvp.validation) {
				this.validationMessage = rsvp.validation;
				return false;
			}

			if (withEvent) {
				this.$emit('save-button-clicked', { source: 'price' });
			}

			return true;
		},
		backClick() {
			this.$router.back();
		},
	},
};
</script>
